import { GetDashboardResponse } from '@blockworks/platform/api/research/studio/dashboard';
import { Bit } from '@blockworks/ui/bit';
import { FlexBox } from '@blockworks/ui/flex-box';
import { Link } from '@blockworks/ui/link';
import { cn } from '@blockworks/ui/system';
import { Text } from '@blockworks/ui/text';

import { useVisualizationOptionsContext } from '../../../../context';
import { ExportExecutionResults } from '../../../export-execution-results';
import { ExpandVisualization } from '../expand-visualization';
import { ShareVisualization } from '../share-visualization';
import { VisualizationHeader } from '../visualization-header';
import type { VisualizationRendererProps } from '../visualization-renderer/visualization-renderer.model';

const RESEARCH_SIGN_UP_URL = 'https://app.blockworksresearch.com/sign-up';
const RESEARCH_SIGN_IN_URL = 'https://app.blockworksresearch.com/sign-in';
const RESEARCH_LANDING_PAGE_URL = 'https://blockworksresearch.com';

type ObscuredVisualizationProps = {
    visualization: Omit<GetDashboardResponse['data']['visualizations'][number], 'lastExecutionId'> & {
        lastExecutionId?: string;
    };
} & Pick<VisualizationRendererProps, 'componentSlots'>;

const ObscuredVisualization = ({ visualization, componentSlots }: ObscuredVisualizationProps) => {
    const { layout } = useVisualizationOptionsContext();
    const { HeaderTitle } = componentSlots ?? {};
    const { title, obscuredScreenshotUrl: src, id: visualizationId, queryId, slug } = visualization;

    return (
        <FlexBox w="full" h="full" col pos="relative">
            <FlexBox
                justifyContent="between"
                alignItems="center"
                borderBottom={1}
                px={2}
                style={{ height: `${layout.headerHeight}px` } as React.CSSProperties}
            >
                <VisualizationHeader
                    slug={slug}
                    title={{ label: title, Component: HeaderTitle }}
                    description={visualization.description}
                    subTitle={visualization.subTitle}
                    visualizationId={visualizationId}
                    queryId={queryId}
                />
                <FlexBox gap={1}>
                    <ShareVisualization isGated trackVisualizationFallback={visualization} />
                    <ExportExecutionResults isGated trackVisualizationFallback={visualization} />
                    <ExpandVisualization isGated trackVisualizationFallback={visualization} />
                </FlexBox>
            </FlexBox>
            <FlexBox w="full" pos="relative" className={cn('h-[calc(100%-43px)]')}>
                {src && (
                    <Bit.div
                        pos="absolute"
                        top={0}
                        left={0}
                        w="full"
                        h="full"
                        style={{
                            backgroundImage: `url(${src})`,
                            backgroundSize: '100% 100%',
                            opacity: 0.35,
                        }}
                    />
                )}
                {cta}
            </FlexBox>
        </FlexBox>
    );
};

const cta = (
    <FlexBox w="full" h="full" center col gap={4}>
        <FlexBox col center>
            <Text size="lg" weight="semibold" center>
                Subscribe to Research to view this metric
            </Text>
            <Text size="sm" center>
                Learn more about Blockworks Research{' '}
                <Link
                    size="sm"
                    intent="primary"
                    textDecoration="underline"
                    target="_blank"
                    href={RESEARCH_LANDING_PAGE_URL}
                >
                    here
                </Link>
            </Text>
        </FlexBox>
        <FlexBox gap={4}>
            <Link
                borderRadius="none"
                textTransform="uppercase"
                size="sm"
                intent="neutral"
                variant="fill"
                target="_blank"
                href={RESEARCH_SIGN_IN_URL}
            >
                Log in
            </Link>
            <Link
                borderRadius="none"
                textTransform="uppercase"
                size="sm"
                intent="primary"
                variant="fill"
                target="_blank"
                href={RESEARCH_SIGN_UP_URL}
            >
                Sign up
            </Link>
        </FlexBox>
    </FlexBox>
);

export { ObscuredVisualization };
